/* eslint-disable @typescript-eslint/no-explicit-any */
import {DestroyRef, Injectable} from '@angular/core';
import {NgEventBus} from 'ng-event-bus';
import {LookupService} from '../../../../../common/service/lookup.service';
import {LineService} from '../../../../services/features/line.service';
import {SpatialUtilService} from '../../../../services/spatial-util.service';
import {LayerUtilService} from '../../../../layers/layer-util.service';
import {MapToolsService} from '../../../../services/map-tools.service';
import {EsriModuleService} from '../../../../esri-nlr/services/esri-module.service';
import {MapService} from '../../../../services/map.service';
import {of} from 'rxjs';
import {LineTypeLookup} from '../../../../../common/models/line-type.model';
import {LineSymbologyLookup} from '../../../../../common/models/line-symbology.model';
import {ConstructionToolModelService} from './construction-tool-model.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ToolServiceInterface} from './tool-service-interface';

@Injectable({
    providedIn: 'root'
})
export class PolylineConstructionToolService implements ToolServiceInterface {
    title = 'PolylineConstructionToolService';

    attributes: any;

    private mapEvents: {unload?: any} = {};

    private points = [];

    private polylineGraphic = null;

    private lastPolylineSegmentGraphic = null;

    private lineSymbol: any;

    private markerSymbol: any;

    private lineSymbologyLookup: LineSymbologyLookup;

    private polylineTypeLookup: LineTypeLookup;

    constructor(
        private constructionToolModelService: ConstructionToolModelService,
        private esriModuleService: EsriModuleService,
        private eventBus: NgEventBus,
        private layerUtilService: LayerUtilService,
        private lookupService: LookupService,
        private lineService: LineService,
        private mapService: MapService,
        private mapToolsService: MapToolsService,
        private spatialUtilService: SpatialUtilService,
        private destroyRef: DestroyRef
    ) {}

    setupFeatureEventModification() {
        console.info(`${this.title} - setupFeatureEventModification`);

        this.eventBus
            .on('constructionToolService:polyline:clear')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.mapToolsService.deactivate('CONSTRUCTION_TOOLS_POLYLINE');
                this.clear();
            });

        this.eventBus
            .on('constructionTool:polyline:close')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.clear();
            });

        this.eventBus
            .on('polyline:typeChange')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                console.debug(`${this.title} - polyLineTypeChange`);
                this.updatePolyLine();
            });
    }

    updatePolyLine() {
        console.debug(`${this.title} - updatePolyLine`);

        this.attributes = this.getAttributesMap();

        this.populateSymbology();

        if (this.polylineGraphic) {
            this.spatialUtilService.removeMapGraphic(this.polylineGraphic);
            const polyline = new this.esriModuleService.geometry.Polyline(this.lastPolylineSegmentGraphic.spatialReference);
            polyline.addPath(this.points.map((p: any) => [p.graphic.geometry.x, p.graphic.geometry.y]));
            this.spatialUtilService.removeMapGraphic(this.lastPolylineSegmentGraphic);
            this.polylineGraphic = new this.esriModuleService.Graphic(polyline, this.lineSymbol);
            this.polylineGraphic.attributes = this.getAttributesMap();
            this.spatialUtilService.addMapGraphic('polyline', this.polylineGraphic);
        } else if (this.lastPolylineSegmentGraphic) {
            this.spatialUtilService.removeMapGraphic(this.lastPolylineSegmentGraphic);
        }
    }

    create(attributes: any) {
        console.info(`${this.title} - create`);

        return this.mapToolsService.activate(
            'CONSTRUCTION_TOOLS_POLYLINE',
            () => {
                this.spatialUtilService.setMapCursor(this.spatialUtilService.getCrossCursorUrl());

                this.attributes = attributes;

                this.mapUnloadHandler();

                this.mapEvents.unload = this.mapEvents.unload || this.getMap().on('unload', () => this.mapUnloadHandler());

                this.init();
            },
            () => {
                this.clear();
                this.eventBus.cast('deactivateConstructionTools');
            }
        );
    }

    finish() {
        const logPrefix = `${this.title} - finish`;
        console.info(logPrefix);

        if (this.polylineGraphic) {
            const line = {
                geometry: this.polylineGraphic.geometry,
                attributes: {
                    guid: this.attributes.guid,
                    type: this.attributes.type.id,
                    pointids: this.points
                        .map((p: any) => {
                            switch (p.featureType) {
                                case 'constructpoint':
                                    return `(${p.feature.attributes.id})`; //round brackets
                                case 'controlpoint':
                                case 'point':
                                default:
                                    return `[${p.feature.attributes.id}]`; //square brackets
                            }
                        })
                        .join()
                }
            };

            console.info(`${logPrefix} - creating line:`, line);

            this.lineService.commitLineFeatureCreation(line).subscribe(createdLines => {
                console.info(`${logPrefix} - lineService.commitLineFeatureCreation response:`, createdLines);
            });

            this.clear();

            this.eventBus.cast('toggleConstructionToolsCreateButton', false);
        } else {
            this.clear();
        }

        this.mapToolsService.deactivate('CONSTRUCTION_TOOLS_POLYLINE');

        return of(null);
    }

    private getAttributesMap() {
        return this.constructionToolModelService.getAttributesMap('polyline');
    }

    private mapClickHandler(evt: any) {
        const logPrefix = `${this.title} - mapClickHandler`;
        console.info(logPrefix);

        this.mapService.snappingManagerTool?.getSnappingPoint(evt.screenPoint).then((sp: any) => {
            if (sp && !this.points.some((p: any) => sp.x === p.graphic.geometry.x && sp.y === p.graphic.geometry.y)) {
                const controlPointFeature = this.spatialUtilService.findControlPointByCoords(sp.x, sp.y);

                const constructPointFeature = this.spatialUtilService.findConstructPointByCoords(sp.x, sp.y);

                const pointFeature = this.spatialUtilService.findPointByCoords(sp.x, sp.y);

                const feature = controlPointFeature || constructPointFeature || pointFeature;

                if (
                    this.polylineTypeLookup.validPointTypes.indexOf(
                        constructPointFeature ? constructPointFeature.attributes.marktype : feature.attributes.type
                    ) === -1
                ) {
                    console.debug(
                        '%s invalid point type:%d',
                        logPrefix,
                        constructPointFeature ? constructPointFeature.attributes.marktype : feature.attributes.type
                    );
                    return;
                }

                const featureClass = (controlPointFeature && 'controlpoint') || (constructPointFeature && 'constructpoint') || 'point';

                const g = new this.esriModuleService.Graphic(sp, this.markerSymbol);
                g.attributes = structuredClone(feature.attributes);

                this.points.push({
                    graphic: g,
                    featureType: (controlPointFeature && 'controlpoint') || (constructPointFeature && 'constructpoint') || 'point',
                    feature: feature
                });

                this.spatialUtilService.addMapGraphic(featureClass, g);

                if (this.points.length >= 2) {
                    if (this.polylineGraphic) {
                        this.spatialUtilService.removeMapGraphic(this.polylineGraphic);
                    }

                    const polyline = new this.esriModuleService.geometry.Polyline(sp.spatialReference);
                    polyline.addPath(this.points.map((p: any) => [p.graphic.geometry.x, p.graphic.geometry.y]));

                    this.polylineGraphic = new this.esriModuleService.Graphic(polyline, this.lineSymbol);
                    this.polylineGraphic.attributes = {};

                    this.spatialUtilService.addMapGraphic('polyline', this.polylineGraphic);
                }
            }
        });
    }

    private mapDblClickHandler() {
        this.finish().subscribe();
    }

    private mapMouseMoveHandler(evt: any) {
        const logPrefix = `${this.title} - mapMouseMoveHandler`;
        console.debug(logPrefix);

        if (this.points.length) {
            if (this.lastPolylineSegmentGraphic) {
                this.spatialUtilService.removeMapGraphic(this.lastPolylineSegmentGraphic);
            }

            const lastPolyline = new this.esriModuleService.geometry.Polyline(evt.mapPoint.spatialReference);

            const lastPointGraphic = this.points[this.points.length - 1].graphic;

            lastPolyline.addPath([
                [lastPointGraphic.geometry.x, lastPointGraphic.geometry.y],
                [evt.mapPoint.x, evt.mapPoint.y]
            ]);

            this.lastPolylineSegmentGraphic = new this.esriModuleService.Graphic(lastPolyline, this.lineSymbol);

            this.spatialUtilService.addMapGraphic(null, this.lastPolylineSegmentGraphic);
        }
    }

    private getMap() {
        return this.spatialUtilService.getMap();
    }

    private mapUnloadHandler() {
        this.detachMapEvents();
    }

    private attachMapEventHandler(eventName: string, eventHandler: any) {
        this.mapEvents[eventName] = this.mapEvents[eventName] || this.getMap().on(eventName, eventHandler);
    }

    private detachMapEvents() {
        Object.keys(this.mapEvents).forEach((k: string) => {
            this.mapEvents[k].remove();
            delete this.mapEvents[k];
        });
    }

    private attachMapEvents() {
        this.detachMapEvents();

        this.attachMapEventHandler('click', (evt: any) => this.mapClickHandler(evt));
        this.attachMapEventHandler('dbl-click', () => this.mapDblClickHandler());
        this.attachMapEventHandler('mouse-move', (evt: any) => this.mapMouseMoveHandler(evt));
    }

    private clear() {
        console.info(`${this.title} - clear`);

        this.points.forEach((p: any) => this.spatialUtilService.removeMapGraphic(p.graphic));
        this.points.length = 0;

        if (this.polylineGraphic) {
            this.spatialUtilService.removeMapGraphic(this.polylineGraphic);
            this.polylineGraphic = null;
        }

        if (this.lastPolylineSegmentGraphic) {
            this.spatialUtilService.removeMapGraphic(this.lastPolylineSegmentGraphic);
            this.lastPolylineSegmentGraphic = null;
        }

        this.detachMapEvents();
        this.mapService.disableSnappingTool();
        this.getMap().enableDoubleClickZoom();
    }

    private init() {
        console.info(`${this.title} - init`);

        this.getMap().disableDoubleClickZoom();

        this.populateSymbology();

        this.mapService.disableMeasurementTool();

        this.mapService.disableSnappingTool();
        this.mapService.enableSnappingTool(
            [
                this.spatialUtilService.getPointMapLayer(),
                this.spatialUtilService.getControlPointMapLayer(),
                this.spatialUtilService.getConstructPointMapLayer()
            ],
            {
                ctrlKeyDisablesSnapping: this.polylineTypeLookup.ctrlKeyDisablesSnapping,
                snapToEdge: false,
                snapToPoint: true,
                tolerance: 30
            }
        );
        this.attachMapEvents();

        this.markerSymbol = new this.esriModuleService.PictureMarkerSymbol('/components/design/esri-nlr/images/landgate-pin.png', 17, 26);
        this.markerSymbol.setOffset(0, 13);

        this.mapService.enableMeasurementTool('distance', {
            lineSymbol: new this.esriModuleService.SimpleLineSymbol(
                this.esriModuleService.SimpleLineSymbol.STYLE_NULL,
                new this.esriModuleService.Color([0, 0, 0, 0.0]),
                1
            ),
            pointSymbol: new this.esriModuleService.SimpleMarkerSymbol(
                this.esriModuleService.SimpleMarkerSymbol.STYLE_CROSS,
                1,
                new this.esriModuleService.SimpleLineSymbol(
                    this.esriModuleService.SimpleLineSymbol.STYLE_NULL,
                    new this.esriModuleService.Color([0, 0, 0, 0.0]),
                    1
                ),
                new this.esriModuleService.Color([0, 0, 0, 0.0])
            )
        });
    }

    private populateSymbology() {
        console.info(`${this.title} - populateSymbology`);

        this.polylineTypeLookup = this.lookupService.getLookups('polylineType').find((l: LineTypeLookup) => l.id === this.attributes.type.id);
        if (!this.polylineTypeLookup) {
            console.error(`${this.title} - populateSymbology - polyline lookup not found for type: ${this.attributes.type.id}`);
            return;
        }

        this.lineSymbologyLookup = this.lookupService
            .getLookups('lineSymbology')
            .find(
                (l: LineSymbologyLookup) =>
                    l.type === this.attributes.type.id && l.viewMode.toLowerCase() === this.spatialUtilService.getMapBackground()
            );

        const lineColor = new this.esriModuleService.Color(this.lineSymbologyLookup.color.split(',').map(Number));
        const lineWidth = this.lineSymbologyLookup.width * this.layerUtilService.getPixelScaleFactor();
        this.lineSymbol = new this.esriModuleService.SimpleLineSymbol(this.esriModuleService.SimpleLineSymbol.STYLE_SOLID, lineColor, lineWidth);
    }
}
